import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { servicesAPI, vehiclesAPI, enumsAPI, partsAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Loader2, Save, Trash2, Wrench, CalendarIcon, Upload, X, Image as ImageIcon, Paperclip, Package } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CATEGORY_TITLES = {
  "Oil/Filter": "Oil & Filter Change",
  "Coolant": "Coolant Flush",
  "Brakes": "Brake Service",
  "Transmission": "Transmission Service",
  "Transfer Case": "Transfer Case Service",
  "Front Differential": "Front Diff Service",
  "Rear Differential": "Rear Diff Service",
  "Wheel Bearings": "Wheel Bearing Service",
  "U-Joints/Driveshaft": "U-Joint/Driveshaft Service",
  "Steering": "Steering Service",
  "Suspension": "Suspension Service",
  "Electrical": "Electrical Work",
  "Tires": "Tire Service",
  "Inspection": "Vehicle Inspection",
  "Other": "Service",
};

export default function ServiceForm() {
  const { id, vehicleId, serviceId } = useParams();
  const actualVehicleId = id || vehicleId;
  const navigate = useNavigate();
  const isEdit = Boolean(serviceId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]); // [{partId, quantity}]
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    vehicle_id: actualVehicleId,
    category: "",
    title: "",
    service_date: (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })(),
    service_mileage: "",
    notes: "",
    fluids_used: "",
    parts_used: "",
    labor_type: "DIY",
    shop_name: "",
    labor_cost: "",
    total_cost: "",
    interval_miles: "",
    interval_months: "",
    reminder_miles_before: "",
    reminder_days_before: "",
  });

  useEffect(() => {
    loadData();
  }, [actualVehicleId, serviceId]);

  const loadData = async () => {
    try {
      const [vehicleRes, categoriesRes, partsRes] = await Promise.all([
        vehiclesAPI.get(actualVehicleId),
        enumsAPI.serviceCategories(),
        partsAPI.list({ vehicle_id: actualVehicleId }),
      ]);
      setVehicle(vehicleRes.data);
      setCategories(categoriesRes.data);
      
      // Load parts - include both vehicle-specific and general parts
      const allPartsRes = await partsAPI.list();
      const vehicleParts = partsRes.data.parts || [];
      const allParts = allPartsRes.data.parts || [];
      // Combine and dedupe parts (vehicle-specific + general inventory)
      const combinedParts = [...vehicleParts];
      allParts.forEach(p => {
        if (!combinedParts.find(vp => vp.id === p.id)) {
          combinedParts.push(p);
        }
      });
      setAvailableParts(combinedParts.filter(p => p.quantity > 0));

      // Pre-fill mileage from vehicle
      if (!isEdit) {
        setForm((prev) => ({
          ...prev,
          service_mileage: vehicleRes.data.mileage_current || "",
        }));
      }

      if (isEdit) {
        const serviceRes = await servicesAPI.get(serviceId);
        const s = serviceRes.data;
        setForm({
          vehicle_id: actualVehicleId,
          category: s.category || "",
          title: s.title || "",
          service_date: s.service_date?.split("T")[0] || "",
          service_mileage: s.service_mileage || "",
          notes: s.notes || "",
          fluids_used: s.fluids_used || "",
          parts_used: s.parts_used || "",
          labor_type: s.labor_type || "DIY",
          shop_name: s.shop_name || "",
          labor_cost: s.labor_cost || "",
          total_cost: s.total_cost || "",
          interval_miles: s.interval_miles || "",
          interval_months: s.interval_months || "",
          reminder_miles_before: s.reminder_miles_before || "",
          reminder_days_before: s.reminder_days_before || "",
        });
        setAttachments(s.attachments || []);
      }
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/vehicles/${actualVehicleId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      if (isEdit && serviceId) {
        const { data } = await servicesAPI.uploadAttachment(serviceId, file);
        setAttachments((prev) => [...prev, data.url]);
        toast.success("Attachment uploaded");
      } else {
        // Convert to base64 for new services
        const reader = new FileReader();
        reader.onload = (event) => {
          setAttachments((prev) => [...prev, event.target.result]);
          toast.success("Attachment added");
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toast.error("Failed to upload attachment");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (category) => {
    const autoTitle = CATEGORY_TITLES[category] || category;
    setForm((prev) => ({
      ...prev,
      category,
      title: prev.title || autoTitle,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        service_mileage: form.service_mileage ? parseInt(form.service_mileage) : 0,
        labor_cost: form.labor_cost ? parseFloat(form.labor_cost) : 0,
        total_cost: form.total_cost ? parseFloat(form.total_cost) : 0,
        interval_miles: form.interval_miles ? parseInt(form.interval_miles) : null,
        interval_months: form.interval_months ? parseInt(form.interval_months) : null,
        reminder_miles_before: form.reminder_miles_before ? parseInt(form.reminder_miles_before) : null,
        reminder_days_before: form.reminder_days_before ? parseInt(form.reminder_days_before) : null,
        attachments: attachments,
        parts_from_inventory: selectedParts.filter(p => p.quantity > 0), // Include selected parts
      };

      if (isEdit) {
        await servicesAPI.update(serviceId, payload);
        toast.success("Service record updated");
      } else {
        await servicesAPI.create(payload);
        // Deduct parts from inventory
        for (const part of selectedParts.filter(p => p.quantity > 0)) {
          try {
            await partsAPI.adjustQuantity(part.partId, -part.quantity);
          } catch (err) {
            console.error("Failed to deduct part:", err);
          }
        }
        if (selectedParts.filter(p => p.quantity > 0).length > 0) {
          toast.success("Service record created & parts deducted from inventory");
        } else {
          toast.success("Service record created");
        }
      }
      navigate(`/vehicles/${actualVehicleId}/maintenance`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service record");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await servicesAPI.delete(serviceId);
      toast.success("Service record deleted");
      navigate(`/vehicles/${actualVehicleId}/maintenance`);
    } catch (error) {
      toast.error("Failed to delete service record");
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={`/vehicles/${actualVehicleId}/maintenance`}
              data-testid="back-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <HeaderIcon />

              <div>
                <h1 className="text-lg font-bold text-foreground">
                  {isEdit ? "Edit Service" : "Log Service"}
                </h1>
                <p className="text-xs text-muted-foreground">{vehicle?.nickname}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Details */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Service Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={form.category}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger
                      data-testid="service-category-select"
                      className="bg-zinc-900 border-zinc-800"
                    >
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    data-testid="service-title-input"
                    value={form.title}
                    onChange={(e) => updateField("title", e.target.value)}
                    placeholder="Service title"
                    className="bg-zinc-900 border-zinc-800"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Service Date <span className="text-red-500">*</span>
                  </Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="service-date-btn"
                        className="w-full justify-start text-left font-mono bg-zinc-900 border-zinc-800"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.service_date
                          ? format(new Date(form.service_date + "T00:00:00"), "MMM dd, yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800">
                      <Calendar
                        mode="single"
                        selected={form.service_date ? new Date(form.service_date + "T00:00:00") : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Use local date to avoid timezone shift
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            updateField("service_date", `${year}-${month}-${day}`);
                          }
                          setDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_mileage">Service Mileage</Label>
                  <Input
                    id="service_mileage"
                    data-testid="service-mileage-input"
                    type="number"
                    value={form.service_mileage}
                    onChange={(e) => updateField("service_mileage", e.target.value)}
                    placeholder="Mileage at service"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Labor & Cost */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Labor & Cost</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Labor Type</Label>
                  <Select
                    value={form.labor_type}
                    onValueChange={(v) => updateField("labor_type", v)}
                  >
                    <SelectTrigger
                      data-testid="labor-type-select"
                      className="bg-zinc-900 border-zinc-800"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="DIY">DIY</SelectItem>
                      <SelectItem value="Shop">Shop</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.labor_type === "Shop" && (
                  <div className="space-y-2">
                    <Label htmlFor="shop_name">Shop Name</Label>
                    <Input
                      id="shop_name"
                      data-testid="shop-name-input"
                      value={form.shop_name}
                      onChange={(e) => updateField("shop_name", e.target.value)}
                      placeholder="Shop name"
                      className="bg-zinc-900 border-zinc-800"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="labor_cost">Labor Cost ($)</Label>
                  <Input
                    id="labor_cost"
                    data-testid="labor-cost-input"
                    type="number"
                    step="0.01"
                    value={form.labor_cost}
                    onChange={(e) => updateField("labor_cost", e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_cost">Total Cost ($)</Label>
                  <Input
                    id="total_cost"
                    data-testid="total-cost-input"
                    type="number"
                    step="0.01"
                    value={form.total_cost}
                    onChange={(e) => updateField("total_cost", e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parts & Fluids */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Parts & Fluids</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Parts from Inventory */}
              {availableParts.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Use Parts from Inventory
                  </Label>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 max-h-48 overflow-y-auto space-y-2">
                    {availableParts.map((part) => {
                      const selected = selectedParts.find(p => p.partId === part.id);
                      const isSelected = selected && selected.quantity > 0;
                      return (
                        <div key={part.id} className="flex items-center justify-between gap-2 p-2 rounded-sm hover:bg-zinc-800/50">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Checkbox
                              id={`part-${part.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedParts(prev => [...prev.filter(p => p.partId !== part.id), { partId: part.id, quantity: 1 }]);
                                } else {
                                  setSelectedParts(prev => prev.filter(p => p.partId !== part.id));
                                }
                              }}
                            />
                            <label htmlFor={`part-${part.id}`} className="text-sm truncate cursor-pointer">
                              {part.name}
                              <span className="text-muted-foreground ml-1">({part.quantity} avail)</span>
                            </label>
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Qty:</span>
                              <Input
                                type="number"
                                min="1"
                                max={part.quantity}
                                value={selected?.quantity || 1}
                                onChange={(e) => {
                                  const qty = Math.min(parseInt(e.target.value) || 1, part.quantity);
                                  setSelectedParts(prev => prev.map(p => 
                                    p.partId === part.id ? { ...p, quantity: qty } : p
                                  ));
                                }}
                                className="w-16 h-7 text-sm bg-zinc-800 border-zinc-700"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedParts.filter(p => p.quantity > 0).length > 0 && (
                    <p className="text-xs text-amber-500">
                      ⚠️ {selectedParts.filter(p => p.quantity > 0).reduce((sum, p) => sum + p.quantity, 0)} part(s) will be deducted from inventory when saved
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="fluids_used">Fluids Used</Label>
                <Textarea
                  id="fluids_used"
                  data-testid="fluids-input"
                  value={form.fluids_used}
                  onChange={(e) => updateField("fluids_used", e.target.value)}
                  placeholder="e.g., 6qt Mobil 1 5W-30, 1qt Toyota LF"
                  className="bg-zinc-900 border-zinc-800 min-h-[80px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parts_used">Parts Used (Manual Entry)</Label>
                <Textarea
                  id="parts_used"
                  data-testid="parts-input"
                  value={form.parts_used}
                  onChange={(e) => updateField("parts_used", e.target.value)}
                  placeholder="e.g., OEM oil filter, crush washer (for parts not in inventory)"
                  className="bg-zinc-900 border-zinc-800 min-h-[80px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="service-notes-input"
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Additional notes..."
                  className="bg-zinc-900 border-zinc-800 min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Attachments */}
              {attachments.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {attachments.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Attachment ${index + 1}`}
                        className="w-full h-24 object-cover rounded-sm border border-zinc-800"
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAttachmentUpload}
                  className="hidden"
                  data-testid="attachment-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-attachment-btn"
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Add Attachment
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Upload photos of receipts, parts, or work done. Max 5MB each.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Service Interval */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Service Interval (Optional)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Set intervals to track when this service is due again
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="interval_miles">Interval (miles)</Label>
                  <Input
                    id="interval_miles"
                    data-testid="interval-miles-input"
                    type="number"
                    value={form.interval_miles}
                    onChange={(e) => updateField("interval_miles", e.target.value)}
                    placeholder="e.g., 5000"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interval_months">Interval (months)</Label>
                  <Input
                    id="interval_months"
                    data-testid="interval-months-input"
                    type="number"
                    value={form.interval_months}
                    onChange={(e) => updateField("interval_months", e.target.value)}
                    placeholder="e.g., 6"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
              
              {/* Custom Reminder Thresholds */}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-sm text-muted-foreground mb-3">
                  Custom reminder thresholds (leave blank for defaults: 500 miles / 30 days)
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="reminder_miles_before">Remind before (miles)</Label>
                    <Input
                      id="reminder_miles_before"
                      data-testid="reminder-miles-input"
                      type="number"
                      value={form.reminder_miles_before}
                      onChange={(e) => updateField("reminder_miles_before", e.target.value)}
                      placeholder="500 (default)"
                      className="bg-zinc-900 border-zinc-800 font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reminder_days_before">Remind before (days)</Label>
                    <Input
                      id="reminder_days_before"
                      data-testid="reminder-days-input"
                      type="number"
                      value={form.reminder_days_before}
                      onChange={(e) => updateField("reminder_days_before", e.target.value)}
                      placeholder="30 (default)"
                      className="bg-zinc-900 border-zinc-800 font-mono"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-4">
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="delete-service-btn"
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Service Record?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this service record. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      data-testid="confirm-delete-service-btn"
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="flex gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/vehicles/${actualVehicleId}/maintenance`)}
                className="border-zinc-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-service-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEdit ? "Save Changes" : "Log Service"}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
